import type { TransitionV2DataQuery } from '@revelio/data-access';

import type { TransitionChartType } from '../../PageConfigProvider/types/chart';
import type { TransitionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { TransitionMetricValues } from '../../PageConfigProvider/types/metric';
import type { TransitionData } from '../TransitionDataProvider';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../types';

export const convertTransitionData = ({
  metric,
  secondaryDimension,
  data,
  chartType,
}: {
  metric: TransitionMetricValues;
  secondaryDimension?: TransitionSecondaryDimensionValues;
  chartType: TransitionChartType;
  data: NonNullable<TransitionV2DataQuery['transitionsV2']>;
}): TransitionData => {
  if (secondaryDimension) {
    const isInflow = metric === 'transitions_inflow';

    // For table views, include ALL selected entities and carry the primary entity label
    if (chartType === 'table') {
      const buildItems = (
        kind: 'current' | 'flow'
      ): Array<SingleDataPoint<{ primary: string }>> =>
        data.entities.flatMap((entity) => {
          const primaryLabel = entity.metadata?.short_name;
          const breakdowns =
            entity.metrics?.breakdowns?.[secondaryDimension] ?? [];
          return breakdowns
            .map((breakdown): SingleDataPoint<{ primary: string }> => {
              const value = (() => {
                if (kind === 'current') return breakdown.current_count;
                if (isInflow) return breakdown.inflows_count;
                return breakdown.outflows_count;
              })();

              const share = (() => {
                if (kind === 'current') return breakdown.current_share;
                if (isInflow) return breakdown.inflows_share;
                return breakdown.outflows_share;
              })();

              return {
                dim: breakdown.short_name,
                value,
                share,
                primary: primaryLabel,
              };
            })
            .filter(
              (item) =>
                item.dim &&
                item.dim.toLowerCase() !== 'unknown' &&
                item.dim.toLowerCase() !== 'empty'
            );
        });

      const currentItems = buildItems('current');
      const flowItems = buildItems('flow');

      return {
        type: DataType.BREAKDOWN_SINGLE,
        data: [
          { dim: 'Current', breakdown: currentItems },
          { dim: isInflow ? 'Inflow' : 'Outflow', breakdown: flowItems },
        ],
      };
    }

    // Default behaviour (non-table charts) – use the first entity and top grouping
    const entity = data?.entities?.[0];

    const currentEntityMeasures = entity?.metrics?.breakdowns?.[
      secondaryDimension
    ]
      ?.map((breakdown): SingleDataPoint => {
        return {
          dim: breakdown.short_name,
          value: breakdown.current_count,
          share: breakdown.current_share,
        };
      })
      .filter(
        (item) =>
          item.dim &&
          item.dim.toLowerCase() !== 'unknown' &&
          item.dim.toLowerCase() !== 'empty'
      );

    const flowEntityMeasures = entity?.metrics?.breakdowns?.[secondaryDimension]
      ?.map((breakdown): SingleDataPoint => {
        return {
          dim: breakdown.short_name,
          value: isInflow ? breakdown.inflows_count : breakdown.outflows_count,
          share: isInflow ? breakdown.inflows_share : breakdown.outflows_share,
        };
      })
      .filter(
        (item) =>
          item.dim &&
          item.dim.toLowerCase() !== 'unknown' &&
          item.dim.toLowerCase() !== 'empty'
      );

    const shouldSort = ['role', 'geography', 'rics'].includes(
      secondaryDimension
    );
    const sortedCurrentMeasures = shouldSort
      ? [...(currentEntityMeasures ?? [])].sort(
          (a, b) => (b.share ?? 0) - (a.share ?? 0)
        )
      : currentEntityMeasures;
    const sortedFlowMeasures = shouldSort
      ? [...(flowEntityMeasures ?? [])].sort(
          (a, b) => (b.share ?? 0) - (a.share ?? 0)
        )
      : flowEntityMeasures;

    const topCurrentItems = sortedCurrentMeasures?.slice(0, 7);
    const topItemIds = topCurrentItems?.map((item) => item.dim);
    const otherCurrentItems = sortedCurrentMeasures?.slice(7);

    const topFlowItems = sortedFlowMeasures?.filter((item) =>
      topItemIds?.includes(item.dim)
    );
    const otherFlowItems = sortedFlowMeasures?.filter(
      (item) => !topItemIds?.includes(item.dim)
    );

    const otherCurrentTotal = otherCurrentItems?.reduce(
      (sum, item) => sum + (item.value ?? 0),
      0
    );
    const otherFlowTotal = otherFlowItems?.reduce(
      (sum, item) => sum + (item.value ?? 0),
      0
    );

    const allCurrentItems: SingleDataPoint[] = [
      ...(topCurrentItems ?? []),
      ...(otherCurrentTotal && otherCurrentTotal > 0
        ? [{ dim: 'Other', value: otherCurrentTotal }]
        : []),
    ];
    const allFlowItems: SingleDataPoint[] = [
      ...(topFlowItems ?? []),
      ...(otherFlowTotal && otherFlowTotal > 0
        ? [{ dim: 'Other', value: otherFlowTotal }]
        : []),
    ];

    return {
      type: DataType.BREAKDOWN_SINGLE,
      data: [
        { dim: 'Current', breakdown: allCurrentItems },
        { dim: isInflow ? 'Inflow' : 'Outflow', breakdown: allFlowItems },
      ],
    };
  }

  const flowMetricName: keyof NonNullable<
    TransitionV2DataQuery['transitionsV2']
  >['entities'][number]['metrics']['flows'] = (() => {
    switch (metric) {
      case 'transitions_inflow':
        return 'inflows';
      case 'transitions_outflow':
        return 'outflows';
    }
  })();
  const transitionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
    (entity) => {
      const metadata = entity.metadata;
      const dim = metadata?.short_name;

      const breakdown: SingleDataPoint[] = entity.metrics?.flows?.[
        flowMetricName
      ]
        ?.filter(
          (flow) =>
            flow.short_name &&
            flow.short_name.toLowerCase() !== 'empty' &&
            flow.short_name.toLowerCase() !== 'unknown'
        )
        ?.map((flow) => ({
          dim: flow.short_name,
          value: flow.count,
          share: flow.share / 100,
        }));

      return { dim, breakdown };
    }
  );

  return { type: DataType.BREAKDOWN_SINGLE, data: transitionData };
};
